/**
 * The chat side of annotate mode — how a slide's staged notes become part of an outgoing message.
 *
 * Ported from `DeckGen/artifactAgent/withAnnotations.ts`, minus that file's iframe INJECT script:
 * the source's oldest path ran the SDK *inside* the sandboxed iframe and posted markdown out via
 * `postMessage`; here the notes never leave the page (they live on `annotateBus`), so the injection
 * and its message parser have nothing to carry. What IS kept, byte-compatible, is everything the
 * chat pipeline recognises:
 *
 * - `ArtifactAnnotationsMessage` and `annotationsToPrompt` — VERBATIM. The `[Pointed annotations…]`
 *   header is a wire format: the host's `stripSystemContext` matches it so persisted prompts do not
 *   print the marker on reload, and the generator prompt was feel-tested against exactly this text.
 *   The `⟨shadow⟩` path stripping rides along too — our own paths never contain host segments (see
 *   `AnnotateLayer`'s path walker), but the regex is kept so any SDK-shaped payload passes through
 *   this function unchanged from the source's behavior.
 * - The markdown body shape — `## Page Feedback:` / `### N. element` / `**Location:**` /
 *   `**Feedback:**` — is the SDK's own `generateOutput` "standard" layout, with one addition from
 *   its "detailed" level: a `**Position:**` line in artifact px on every note. Positions are
 *   load-bearing here (a stage-space note over an iframe has no selector to point with), and the
 *   artifact's fixed 1920x1080 absolute-positioning contract is exactly what the generator writes.
 *
 * The staged form (`annotationsSummary`) is `{ output, count }` — the same compact payload the
 * source's `onPayload` staged on the composer as an attachment card.
 */
import { type AnnotationKey, listAnnotations, type SlideAnnotation } from './annotateBus';

/** The payload shape the source page staged on the chat composer (`onPayload`) — kept verbatim so
 *  the chat side recognises it: `output` is agent-oriented markdown, `annotations` the raw notes. */
export interface ArtifactAnnotationsMessage {
  output: string;
  annotations: unknown[];
}

/** The artifact's own coordinate space — `ARTIFACT_W/H`, restated here so this module stays pure
 *  (no import that drags DOM-flavored modules into the node test environment). */
const SPACE_W = 1920;
const SPACE_H = 1080;

/** Area notes carry the SDK's own vocabulary for its drag-select affordance. */
const AREA_NAME = 'Area selection';

/**
 * The first line of the outgoing block, and a WIRE FORMAT rather than a sentence.
 *
 * `stripSystemContext` in `app/utils/planning-utils.ts` matches this exact opening to take the block off
 * the user's bubble on reload, and `parseAnnotationsBlock` below finds the block by it. Three places, one
 * string — so it is a constant, and changing it means changing the stripper in the same commit.
 */
const PROMPT_HEADER =
  '[Pointed annotations on the artifact — each one is an edit requirement on the exact element it names]';

/** One note, read back off a persisted prompt. Positions are artifact px, as written. */
export interface ParsedAnnotation {
  /** The number the pin wore, 1-based, exactly as the reader will draw it again. */
  n: number;
  /** What the note pointed at — an element name, `Area selection`, or a bare coordinate. */
  name: string;
  x: number;
  y: number;
  /** Present together = an area note. */
  w?: number;
  h?: number;
  note: string;
}

const round = (n: number): number => Math.round(n);

/** Human name for a note — hit-tested element name when the layer had one, the SDK's area wording
 *  for rects, a plain coordinate for a point over an unreachable (iframe) document. Takes the
 *  geometry subset so the layer's not-yet-committed pending note can use it for the popup header. */
export function annotationName(note: Pick<SlideAnnotation, 'x' | 'y' | 'w' | 'h' | 'element'>): string {
  if (note.element) return note.element;
  if (note.w != null && note.h != null) return AREA_NAME;
  return `point at (${round(note.x)}, ${round(note.y)})`;
}

/** The `**Location:**` value — the artifact-real path when known; the SDK's own `region at (x, y)`
 *  wording for a rect without one (its empty-area drag produced exactly that string). A hand-edited
 *  target adds its name tag, `.stats > .card [e3]`, so the ask reaches the element the user moved. */
function annotationLocation(note: SlideAnnotation): string {
  const eid = note.eid && /^e[0-9]{1,6}$/.test(note.eid) ? ` [${note.eid}]` : '';
  if (note.elementPath) return `${note.elementPath}${eid}`;
  if (note.w != null && note.h != null) return `region at (${round(note.x)}, ${round(note.y)})`;
  return `point at (${round(note.x)}, ${round(note.y)})${eid}`;
}

/**
 * The current notes for one slide as the staged payload, or null when the board is empty (null is
 * what un-stages the composer card — the source's `onPayload(null)` contract).
 *
 * Pure by construction: reads the bus, formats strings, touches no DOM — the shape is what the
 * node-env tests pin down.
 */
export function annotationsSummary(slideId: AnnotationKey): { output: string; count: number } | null {
  const notes = listAnnotations(slideId);
  if (notes.length === 0) return null;

  // The SDK's `generateOutput` header, with the artifact for a page: its `pathname` named what the
  // notes were ON, and its `Viewport` named the space the numbers were IN. Ours are the artifact and
  // its fixed frame. Same `×`, same bolding — the generator already reads this dialect.
  let output = `## Page Feedback: artifact ${slideId}\n`;
  output += `**Viewport:** ${SPACE_W}×${SPACE_H}\n\n`;

  notes.forEach((note, i) => {
    output += `### ${i + 1}. ${annotationName(note)}\n`;
    output += `**Location:** ${annotationLocation(note)}\n`;
    output +=
      note.w != null && note.h != null
        ? `**Position:** ${round(note.x)}px, ${round(note.y)}px (${round(note.w)}×${round(note.h)}px)\n`
        : `**Position:** ${round(note.x)}px, ${round(note.y)}px\n`;
    output += `**Feedback:** ${note.text}\n\n`;
  });

  return { output: output.trim(), count: notes.length };
}

/**
 * The staged set, read back OUT of a message that already went.
 *
 * The reader of the format `annotationsSummary` writes, and it lives in this file rather than beside it
 * for the one reason that matters: a format with its writer and its reader in different files drifts on
 * the first edit that only remembers one of them. Everything below is anchored to a line that function
 * emits, and the two are ten lines apart.
 *
 * WHY IT HAS TO EXIST. The notes are session-local (see `annotateBus`) and die with the tab, but the
 * PROMPT is persisted — `stripSystemContext` takes this block off the user's bubble on reload precisely
 * because the server still holds it. So after a refresh the only surviving record that a turn pointed at
 * anything is this text, and it is what the turn's artifact reference is rebuilt from.
 *
 * Tolerant by construction: it reads what it recognises and ignores the rest. A note whose `Feedback:`
 * line is missing is dropped rather than returned half-built, an unparseable position falls back to the
 * artifact's centre (a pin somewhere honest beats no pin at all), and a message with no block at all
 * comes back as an empty array. This runs on every user turn in a reloaded thread; it may never throw.
 */
export function parseAnnotationsBlock(content: string): ParsedAnnotation[] {
  if (typeof content !== 'string' || !content.includes(PROMPT_HEADER)) return [];
  const block = content.slice(content.indexOf(PROMPT_HEADER));
  const out: ParsedAnnotation[] = [];
  // Split on the `### N. name` headings the writer emits, keeping each heading with its own body.
  const sections = block.split(/\n(?=### \d+\. )/);
  for (const section of sections) {
    const head = /^### (\d+)\. (.*)$/m.exec(section);
    if (!head) continue;
    const feedback = /^\*\*Feedback:\*\* ([\s\S]*?)(?=\n\n|\n### |$)/m.exec(section);
    // No note, no annotation. The other three lines describe a note; this one IS it.
    if (!feedback) continue;
    const position = /^\*\*Position:\*\* (-?\d+)px, (-?\d+)px(?: \((\d+)×(\d+)px\))?/m.exec(section);
    const x = position ? Number(position[1]) : SPACE_W / 2;
    const y = position ? Number(position[2]) : SPACE_H / 2;
    const w = position && position[3] ? Number(position[3]) : undefined;
    const h = position && position[4] ? Number(position[4]) : undefined;
    out.push({
      n: Number(head[1]),
      name: head[2].trim(),
      x,
      y,
      ...(w != null && h != null ? { w, h } : null),
      note: feedback[1].trim(),
    });
  }
  return out;
}

/**
 * Which artifact the block was written about.
 *
 * `annotationsSummary` opens its body with `## Page Feedback: artifact 7` (prompts persisted before
 * 2026-09-13 say `artifact (slide 7)`, and both still read), and that number is
 * the only thing in a persisted prompt that says WHICH artifact the pins belong to. A reloaded turn
 * needs it to find the thumbnail to draw them on — without it the reference can still show the marks,
 * but only on empty ground.
 *
 * Null when the message carried no block, or carried one written before this line existed.
 */
export function annotationsBlockSlideId(content: string): number | string | null {
  // A deck artifact is a number; a kit composition is its role id ("metric-proof"). Same heading, in
  // its old `artifact (slide 7)` form or its current `artifact 7` form.
  const found = /^## Page Feedback: artifact (?:\(slide ([^)\s]+)\)|([^\s()]+)[ \t]*$)/m.exec(content ?? '');
  if (!found) return null;
  const id = found[1] ?? found[2];
  return /^\d+$/.test(id) ? Number(id) : id;
}

/**
 * Where a note's MARK goes, normalised to 0–1 of the artifact's own box.
 *
 * An area note's mark sits at the centre of its rect and a point note's sits on its point — the same
 * rule `AnnotateLayer` draws live, restated here so a reloaded turn puts its pins exactly where the
 * session that made them did. Clamped, because a note dropped on the very edge of a 1920x1080 canvas
 * would otherwise place a 14px mark half outside the thumbnail.
 */
export function annotationMark(note: Pick<ParsedAnnotation, 'x' | 'y' | 'w' | 'h'>): { x: number; y: number } {
  const cx = note.w != null ? note.x + note.w / 2 : note.x;
  const cy = note.h != null ? note.y + note.h / 2 : note.y;
  const clamp = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5);
  return { x: clamp(cx / SPACE_W), y: clamp(cy / SPACE_H) };
}

/**
 * The chat message a staged annotation set becomes — VERBATIM from the source. The header tells the
 * agent what it is looking at; the body is the note markdown untouched.
 *
 * Layout changes get a correction appended: the SDK writes a "Reference Frame" that infers a content
 * container from whatever element it guessed and phrases every move as "Suggested" — feel-tested
 * 2026-08-26, the generator treated the suggestion as optional and the percentages as truth, and
 * nothing moved. The appendix overrides both, deterministically, before any model reads it. (Our own
 * summaries never emit that section — layout/drag mode is not ported — but the branch stays so an
 * SDK-shaped payload behaves identically to the source.)
 */
export function annotationsToPrompt(message: ArtifactAnnotationsMessage): string {
  // Edit mode in the source rendered the document as a shadow tree, so SDK element paths carried the
  // host prefix (`div > ⟨shadow⟩ div > .stage > h1` — its notation, measured). Strip everything
  // through the shadow marker: the artifact-real path is what the generator must match.
  const output = message.output
    .trim()
    .replace(/[^\s\n*:][^\n*:]*⟨shadow⟩\s*(div\s*>\s*)?|⟨shadow⟩\s*(div\s*>\s*)?/g, '');
  const parts = [PROMPT_HEADER, output];
  if (message.output.includes('Suggested Layout Changes')) {
    parts.push(
      [
        'ARTIFACT COORDINATE RULES (these override the Reference Frame formulas above):',
        'The document is a fixed 1920x1080 frame with absolute pixel positioning. Ignore the container-width percentages and margin formulas above.',
        'Every entry under "Suggested Layout Changes" is a REQUIRED change, not a suggestion. For each one: take the element by its Selector, and move or resize it so its box lands at the "Suggested position" in absolute pixels on the 1920x1080 frame, adjusting the surrounding layout so nothing overlaps and nothing else moves that was not listed.',
        'A "placement" entry means CREATE a new element of the named kind at the given rect, styled by the theme context.',
      ].join('\n'),
    );
  }
  return parts.join('\n\n');
}

/** The slide's staged notes as a ready-to-send agent-only prefix (the chat seam's `prefix` slot),
 *  or null when there is nothing to ride. `send(text, attachments, annotationsPrefix(id))`. */
export function annotationsPrefix(slideId: AnnotationKey): string | null {
  const staged = annotationsSummary(slideId);
  if (!staged) return null;
  return annotationsToPrompt({ output: staged.output, annotations: [] });
}

/** The source file's namesake, restated for the new seam: fold the slide's current notes into an
 *  outgoing instruction. A prompt with no notes comes back untouched — the same "nothing to anchor
 *  on, return the input" contract the source's `withAnnotations(html)` had for partial documents. */
export function withAnnotations(prompt: string, slideId: AnnotationKey): string {
  const prefix = annotationsPrefix(slideId);
  if (!prefix) return prompt;
  return prompt ? `${prefix}\n\n${prompt}` : prefix;
}
